import * as React from 'react';
import { UYUM_TIPLERI, UYUM_TANIMLARI, sonrakiBitis, type UyumTipi } from '@hotelcms/shared';
import { ApiHatasi, api, oturum } from '@/lib/api';

/**
 * Tesis uyum takvimi.
 *
 * Ne ise yarar: denetim geldiginde belgeleri tek ekrandan gostermek ve suresi
 * gecen bir sertifikayi kacirmamak.
 *
 * DURUSTLUK: 26 Agustos 2026 yonetmeligi (1 Kasim 2026'da yururlukte) basit
 * konaklama tesislerine yeni nitelikler getirdi, ama bu kayitlari DIJITAL
 * TUTMAYI zorunlu KILMIYOR. Ekranda hicbir yerde "yasal zorunluluk" demiyoruz;
 * liste de baglayici bir mevzuat listesi degil, yaygin istenen belgelerin
 * hatirlaticisidir. Otelciye olmayan bir yukumluluk anlatmak, urunu
 * satmaktan daha cok zarar verir.
 */

interface Kayit {
  id: string;
  tip: UyumTipi;
  tipAd: string;
  ad: string | null;
  ilgiliKisi: string | null;
  kurum: string | null;
  belgeNo: string | null;
  baslangicTarihi: string | null;
  bitisTarihi: string | null;
  hatirlatmaGun: number;
  not: string | null;
  dosyaVarMi: boolean;
  dosyaAdi: string | null;
  durum: 'GECERLI' | 'YAKLASIYOR' | 'GECTI' | 'TARIH_YOK';
  kalanGun: number | null;
}

interface Veri {
  bugun: string;
  kayitlar: Kayit[];
  ozet: { toplam: number; gecti: number; yaklasiyor: number; tarihYok: number };
}

const DURUM_GORUNUM: Record<Kayit['durum'], { etiket: string; renk: string; zemin: string; sira: number }> = {
  GECTI: { etiket: 'Süresi geçti', renk: '#c0392b', zemin: '#fdeceb', sira: 0 },
  YAKLASIYOR: { etiket: 'Yaklaşıyor', renk: '#b9840f', zemin: '#fff5dc', sira: 1 },
  TARIH_YOK: { etiket: 'Tarih girilmedi', renk: '#5b6480', zemin: '#f1f3f8', sira: 2 },
  GECERLI: { etiket: 'Geçerli', renk: '#12a150', zemin: '#e7f7ee', sira: 3 },
};

function kalanMetin(k: Kayit): string {
  if (k.kalanGun === null) return 'Son tarih girilmedi';
  if (k.kalanGun < 0) return `${-k.kalanGun} gün önce doldu`;
  if (k.kalanGun === 0) return 'Bugün doluyor';
  return `${k.kalanGun} gün kaldı`;
}

function baslik(k: Kayit): string {
  const temel = k.ad?.trim() || k.tipAd;
  return k.ilgiliKisi?.trim() ? `${temel} · ${k.ilgiliKisi.trim()}` : temel;
}

export function UyumTakvimi({ yazabilir }: { yazabilir: boolean }) {
  const [veri, setVeri] = React.useState<Veri | null>(null);
  const [hata, setHata] = React.useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [form, setForm] = React.useState<Partial<Kayit> | null>(null);
  const [yenilenen, setYenilenen] = React.useState<Kayit | null>(null);

  const yukle = React.useCallback(async () => {
    try {
      setVeri(await api<Veri>('/api/uyum'));
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Uyum takvimi alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  const sirali = React.useMemo(() => {
    if (!veri) return [];
    // Once acil olan: suresi gecen, sonra yaklasan. Ekrani acan kisinin
    // yapacagi is en ustte durmali.
    return [...veri.kayitlar].sort((a, b) => {
      const fark = DURUM_GORUNUM[a.durum].sira - DURUM_GORUNUM[b.durum].sira;
      if (fark !== 0) return fark;
      return (a.bitisTarihi ?? '9999').localeCompare(b.bitisTarihi ?? '9999');
    });
  }, [veri]);

  async function sil(k: Kayit) {
    if (!confirm(`"${baslik(k)}" kaydı listeden kaldırılsın mı?`)) return;
    try {
      await api(`/api/uyum/${k.id}`, { method: 'DELETE' });
      await yukle();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Kayıt kaldırılamadı.');
    }
  }

  return (
    <main className="flex flex-1 flex-col gap-4 bg-zemin p-3 sm:gap-[22px] sm:p-7">
      <div className="flex flex-wrap items-center gap-3 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both]">
        <h1 className="text-2xl font-bold tracking-[-.03em] sm:text-3xl">Uyum takvimi</h1>
        {veri && (veri.ozet.gecti > 0 || veri.ozet.yaklasiyor > 0) && (
          <span
            className="rounded-full px-3 py-1.5 text-[13px] font-semibold"
            style={{
              background: veri.ozet.gecti > 0 ? '#fdeceb' : '#fff5dc',
              color: veri.ozet.gecti > 0 ? '#c0392b' : '#b9840f',
            }}
          >
            {veri.ozet.gecti > 0 && `${veri.ozet.gecti} belge süresi geçti`}
            {veri.ozet.gecti > 0 && veri.ozet.yaklasiyor > 0 && ' · '}
            {veri.ozet.yaklasiyor > 0 && `${veri.ozet.yaklasiyor} belge yaklaşıyor`}
          </span>
        )}
        {yazabilir && (
          <button
            onClick={() => setForm({ tip: 'TURIZM_BELGESI', hatirlatmaGun: 30 })}
            className="ml-auto flex items-center gap-2 rounded-xl bg-marka-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_-14px_rgba(31,79,216,.8)] transition hover:bg-marka-700"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Belge ekle
          </button>
        )}
      </div>

      {/*
        Dogruyu yazan tek satir. Otelciye "kanun bunu istiyor" demek kolay ve
        satisa yarar; yanlis oldugu icin yazmiyoruz.
      */}
      <p className="rounded-2xl border border-cizgi bg-white px-4 py-3 text-[13px] leading-relaxed text-gri">
        Bu liste, konaklama tesislerinde yaygın olarak istenen belgelerin hatırlatıcısıdır; bağlayıcı bir
        mevzuat listesi değildir ve bu kayıtları dijital tutma zorunluluğu yoktur. Amacı denetimde belgelerinizi
        tek ekrandan gösterebilmeniz ve süresi dolan bir belgeyi kaçırmamanız. Gerçek süre her zaman belgenin
        kendi üzerinde yazar.
      </p>

      {hata && (
        <p role="alert" className="rounded-2xl border border-kirmizi/30 bg-[#fdeceb] px-4 py-3 text-sm font-medium text-kirmizi">
          {hata}
        </p>
      )}

      {yukleniyor ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-2xl border border-cizgi bg-white" />
          ))}
        </div>
      ) : sirali.length === 0 ? (
        <div className="rounded-[20px] border border-cizgi bg-white p-8 text-center">
          <p className="text-[15px] font-semibold">Henüz belge eklenmedi.</p>
          <p className="mx-auto mt-2 max-w-lg text-sm text-gri">
            Turizm belgesi, ilk yardım sertifikası, ilaçlama, yangın tüpü dolumu, asansör kontrolü gibi
            belgelerin son tarihlerini girin; süre dolmadan önce e-posta ile hatırlatırız.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {sirali.map((k) => {
            const g = DURUM_GORUNUM[k.durum];
            return (
              <div key={k.id} className="flex flex-col gap-2.5 rounded-2xl border border-cizgi bg-white p-4">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold tracking-[-.01em]">{baslik(k)}</p>
                    {/* Baslik zaten tur adiysa alt satirda tekrarlamayalim. */}
                    {baslik(k) !== k.tipAd && <p className="mt-0.5 text-[12.5px] text-gri">{k.tipAd}</p>}
                  </div>
                  <span
                    className="shrink-0 rounded-full px-2.5 py-1 text-[11.5px] font-semibold"
                    style={{ background: g.zemin, color: g.renk }}
                  >
                    {g.etiket}
                  </span>
                </div>

                {/* Tarih yoksa rozet zaten soyluyor; ayni cumleyi iki kez yazmayiz. */}
                {k.bitisTarihi && (
                  <div className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
                    <span className="font-semibold" style={{ color: g.renk }}>
                      {kalanMetin(k)}
                    </span>
                    <span className="text-gri">· son tarih {k.bitisTarihi}</span>
                  </div>
                )}

                {(k.kurum || k.belgeNo) && (
                  <p className="text-[12.5px] text-gri">
                    {[k.kurum, k.belgeNo && `No: ${k.belgeNo}`].filter(Boolean).join(' · ')}
                  </p>
                )}
                {k.not && <p className="text-[12.5px] italic text-gri">{k.not}</p>}

                <div className="mt-auto flex flex-wrap gap-2 pt-1">
                  {k.dosyaVarMi ? (
                    <BelgeAc kayit={k} />
                  ) : (
                    yazabilir && <DosyaSec kayit={k} bitti={yukle} setHata={setHata} />
                  )}
                  {yazabilir && (
                    <>
                      <button
                        onClick={() => setYenilenen(k)}
                        className="rounded-xl border border-cizgi px-3 py-1.5 text-[12.5px] font-semibold text-lacivert hover:border-lacivert"
                      >
                        Yenilendi
                      </button>
                      <button
                        onClick={() => setForm(k)}
                        className="rounded-xl border border-cizgi px-3 py-1.5 text-[12.5px] font-semibold text-gri hover:border-lacivert hover:text-lacivert"
                      >
                        Düzelt
                      </button>
                      <button
                        onClick={() => void sil(k)}
                        className="rounded-xl border border-cizgi px-3 py-1.5 text-[12.5px] font-semibold text-gri hover:border-kirmizi hover:text-kirmizi"
                      >
                        Kaldır
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <BelgeFormu
          kayit={form}
          kapat={() => setForm(null)}
          bitti={async () => {
            setForm(null);
            await yukle();
          }}
        />
      )}
      {yenilenen && (
        <YenileDialog
          kayit={yenilenen}
          kapat={() => setYenilenen(null)}
          bitti={async () => {
            setYenilenen(null);
            await yukle();
          }}
        />
      )}
    </main>
  );
}

/**
 * Belgeyi yeni sekmede acar.
 *
 * Duz <a href> ise yaramaz: uc JWT istiyor ve tarayici yeni sekmede
 * Authorization basligi gondermez. Bu yuzden blob olarak cekip aciyoruz.
 */
function BelgeAc({ kayit }: { kayit: Kayit }) {
  const [aciliyor, setAciliyor] = React.useState(false);

  async function ac() {
    setAciliyor(true);
    try {
      const cevap = await fetch(`/api/uyum/${kayit.id}/dosya`, {
        headers: { authorization: `Bearer ${oturum.token() ?? ''}` },
      });
      if (!cevap.ok) throw new Error('acilamadi');
      const url = URL.createObjectURL(await cevap.blob());
      window.open(url, '_blank', 'noopener');
      // Sekme kendi kopyasini aldi; bellegi tutmaya gerek yok.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      alert('Belge açılamadı. Sayfayı yenileyip tekrar deneyin.');
    } finally {
      setAciliyor(false);
    }
  }

  return (
    <button
      onClick={() => void ac()}
      disabled={aciliyor}
      className="rounded-xl border border-cizgi bg-zemin px-3 py-1.5 text-[12.5px] font-semibold text-lacivert hover:border-lacivert disabled:opacity-60"
    >
      {aciliyor ? 'Açılıyor…' : 'Belgeyi göster'}
    </button>
  );
}

function DosyaSec({
  kayit,
  bitti,
  setHata,
}: {
  kayit: Kayit;
  bitti: () => Promise<void>;
  setHata: (m: string) => void;
}) {
  const girdi = React.useRef<HTMLInputElement>(null);
  const [yukleniyor, setYukleniyor] = React.useState(false);

  async function sec(dosya: File) {
    setYukleniyor(true);
    try {
      const form = new FormData();
      form.append('dosya', dosya);
      await api(`/api/uyum/${kayit.id}/dosya`, { method: 'POST', body: form });
      await bitti();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Dosya yüklenemedi.');
    } finally {
      setYukleniyor(false);
    }
  }

  return (
    <>
      <input
        ref={girdi}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={(e) => {
          const d = e.target.files?.[0];
          if (d) void sec(d);
          e.target.value = '';
        }}
      />
      <button
        onClick={() => girdi.current?.click()}
        disabled={yukleniyor}
        className="rounded-xl border border-dashed border-cizgi px-3 py-1.5 text-[12.5px] font-semibold text-gri hover:border-lacivert hover:text-lacivert disabled:opacity-60"
      >
        {yukleniyor ? 'Yükleniyor…' : 'Belge fotoğrafı ekle'}
      </button>
    </>
  );
}

/** Belge yenilendi: bitis tarihi tipin periyodundan onerilir, elle degistirilebilir. */
function YenileDialog({ kayit, kapat, bitti }: { kayit: Kayit; kapat: () => void; bitti: () => Promise<void> }) {
  const bugun = new Date().toISOString().slice(0, 10);
  const [baslangic, setBaslangic] = React.useState(bugun);
  const oneri = sonrakiBitis(kayit.tip, baslangic);
  const [bitis, setBitis] = React.useState(oneri ?? '');
  const [elle, setElle] = React.useState(false);
  const [kaydediliyor, setKaydediliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);

  // Kullanici bitisi elle degistirmediyse oneriyi takip et.
  React.useEffect(() => {
    if (!elle) setBitis(sonrakiBitis(kayit.tip, baslangic) ?? '');
  }, [baslangic, elle, kayit.tip]);

  async function kaydet() {
    setKaydediliyor(true);
    try {
      await api(`/api/uyum/${kayit.id}/yenile`, {
        method: 'POST',
        body: JSON.stringify({ baslangicTarihi: baslangic, bitisTarihi: bitis || null }),
      });
      await bitti();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Kaydedilemedi.');
      setKaydediliyor(false);
    }
  }

  return (
    <Ortu kapat={kapat} baslik={`${baslik(kayit)} — yenilendi`}>
      <Alan etiket="Yeni belge tarihi">
        <input type="date" value={baslangic} onChange={(e) => setBaslangic(e.target.value)} className={GIRDI} />
      </Alan>
      <Alan
        etiket="Son geçerlilik tarihi"
        ipucu={
          oneri && !elle
            ? `${UYUM_TANIMLARI[kayit.tip].periyotAy} aylık periyoda göre önerildi — belgede farklı yazıyorsa değiştirin.`
            : 'Belgenin üzerinde yazan tarihi girin.'
        }
      >
        <input
          type="date"
          value={bitis}
          onChange={(e) => {
            setElle(true);
            setBitis(e.target.value);
          }}
          className={GIRDI}
        />
      </Alan>
      {hata && <p role="alert" className="text-sm font-medium text-kirmizi">{hata}</p>}
      <Dugmeler kapat={kapat} kaydet={kaydet} kaydediliyor={kaydediliyor} kapali={!bitis} />
    </Ortu>
  );
}

function BelgeFormu({
  kayit,
  kapat,
  bitti,
}: {
  kayit: Partial<Kayit>;
  kapat: () => void;
  bitti: () => Promise<void>;
}) {
  const duzenleme = Boolean(kayit.id);
  const [tip, setTip] = React.useState<UyumTipi>(kayit.tip ?? 'TURIZM_BELGESI');
  const [ad, setAd] = React.useState(kayit.ad ?? '');
  const [ilgiliKisi, setIlgiliKisi] = React.useState(kayit.ilgiliKisi ?? '');
  const [kurum, setKurum] = React.useState(kayit.kurum ?? '');
  const [belgeNo, setBelgeNo] = React.useState(kayit.belgeNo ?? '');
  const [baslangic, setBaslangic] = React.useState(kayit.baslangicTarihi ?? '');
  const [bitis, setBitis] = React.useState(kayit.bitisTarihi ?? '');
  const [hatirlatmaGun, setHatirlatmaGun] = React.useState(kayit.hatirlatmaGun ?? 30);
  const [not, setNot] = React.useState(kayit.not ?? '');
  const [kaydediliyor, setKaydediliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);

  const tanim = UYUM_TANIMLARI[tip];

  // Yeni kayitta baslangic girilince bitisi periyottan oner; kullanici
  // dokunmussa karismayiz.
  const [bitisDokunuldu, setBitisDokunuldu] = React.useState(duzenleme);
  React.useEffect(() => {
    if (bitisDokunuldu || !baslangic) return;
    const oneri = sonrakiBitis(tip, baslangic);
    if (oneri) setBitis(oneri);
  }, [baslangic, tip, bitisDokunuldu]);

  async function kaydet() {
    setKaydediliyor(true);
    try {
      const govde = {
        tip,
        ad: ad.trim() || null,
        ilgiliKisi: ilgiliKisi.trim() || null,
        kurum: kurum.trim() || null,
        belgeNo: belgeNo.trim() || null,
        baslangicTarihi: baslangic || null,
        bitisTarihi: bitis || null,
        hatirlatmaGun,
        not: not.trim() || null,
      };
      if (duzenleme) {
        await api(`/api/uyum/${kayit.id}`, { method: 'PATCH', body: JSON.stringify(govde) });
      } else {
        await api('/api/uyum', { method: 'POST', body: JSON.stringify(govde) });
      }
      await bitti();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Kaydedilemedi.');
      setKaydediliyor(false);
    }
  }

  return (
    <Ortu kapat={kapat} baslik={duzenleme ? 'Belgeyi düzelt' : 'Belge ekle'}>
      <Alan etiket="Belge türü" ipucu={tanim.aciklama}>
        <select value={tip} onChange={(e) => setTip(e.target.value as UyumTipi)} className={GIRDI}>
          {UYUM_TIPLERI.map((t) => (
            <option key={t} value={t}>
              {UYUM_TANIMLARI[t].ad}
            </option>
          ))}
        </select>
      </Alan>

      {tanim.kisiyeBagli && (
        <Alan etiket="Kimin sertifikası" ipucu="Her personel için ayrı kayıt açın.">
          <input value={ilgiliKisi} onChange={(e) => setIlgiliKisi(e.target.value)} className={GIRDI} placeholder="Ad Soyad" />
        </Alan>
      )}

      <Alan etiket="Başlık" ipucu="Boş bırakırsanız belge türünün adı kullanılır.">
        <input value={ad} onChange={(e) => setAd(e.target.value)} className={GIRDI} placeholder={tanim.ad} />
      </Alan>

      <div className="grid gap-3 sm:grid-cols-2">
        <Alan etiket="Veren kurum / firma">
          <input value={kurum} onChange={(e) => setKurum(e.target.value)} className={GIRDI} />
        </Alan>
        <Alan etiket="Belge no">
          <input value={belgeNo} onChange={(e) => setBelgeNo(e.target.value)} className={GIRDI} />
        </Alan>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Alan etiket="Belge tarihi">
          <input type="date" value={baslangic} onChange={(e) => setBaslangic(e.target.value)} className={GIRDI} />
        </Alan>
        <Alan
          etiket="Son geçerlilik tarihi"
          ipucu={tanim.periyotAy ? `Genelde ${tanim.periyotAy} ayda bir yenilenir.` : 'Süreye bağlı değilse boş bırakın.'}
        >
          <input
            type="date"
            value={bitis}
            onChange={(e) => {
              setBitisDokunuldu(true);
              setBitis(e.target.value);
            }}
            className={GIRDI}
          />
        </Alan>
      </div>

      <Alan etiket="Kaç gün önce hatırlatılsın" ipucu="Süre dolmadan bu kadar gün önce e-posta gönderilir.">
        <select
          value={hatirlatmaGun}
          onChange={(e) => setHatirlatmaGun(Number(e.target.value))}
          className={GIRDI}
        >
          {[7, 15, 30, 60, 90].map((g) => (
            <option key={g} value={g}>
              {g} gün önce
            </option>
          ))}
        </select>
      </Alan>

      <Alan etiket="Not">
        <textarea value={not} onChange={(e) => setNot(e.target.value)} rows={2} className={GIRDI} />
      </Alan>

      {hata && <p role="alert" className="text-sm font-medium text-kirmizi">{hata}</p>}
      <Dugmeler kapat={kapat} kaydet={kaydet} kaydediliyor={kaydediliyor} kapali={false} />
    </Ortu>
  );
}

/* --------------------------------------------------------------- parcalar */

const GIRDI =
  'w-full rounded-xl border border-cizgi bg-white px-3 py-2.5 text-[15px] outline-none focus:border-lacivert';

function Ortu({ kapat, baslik, children }: { kapat: () => void; baslik: string; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-lacivert/40 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={kapat}
    >
      <div
        role="dialog"
        aria-label={baslik}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92dvh] w-full flex-col gap-3 overflow-y-auto rounded-t-[24px] bg-white p-5 sm:max-w-lg sm:rounded-[24px] sm:p-6"
      >
        <h2 className="text-[19px] font-bold tracking-[-.02em]">{baslik}</h2>
        {children}
      </div>
    </div>
  );
}

function Alan({ etiket, ipucu, children }: { etiket: string; ipucu?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13px] font-semibold text-lacivert">{etiket}</span>
      {children}
      {ipucu && <span className="text-[12px] leading-snug text-gri">{ipucu}</span>}
    </label>
  );
}

function Dugmeler({
  kapat,
  kaydet,
  kaydediliyor,
  kapali,
}: {
  kapat: () => void;
  kaydet: () => Promise<void>;
  kaydediliyor: boolean;
  kapali: boolean;
}) {
  return (
    <div className="mt-1 flex gap-2.5">
      <button
        onClick={kapat}
        className="flex-1 rounded-xl border border-cizgi px-4 py-3 text-sm font-semibold text-gri hover:border-lacivert hover:text-lacivert"
      >
        Vazgeç
      </button>
      <button
        onClick={() => void kaydet()}
        disabled={kaydediliyor || kapali}
        className="flex-1 rounded-xl bg-marka-600 px-4 py-3 text-sm font-semibold text-white hover:bg-marka-700 disabled:opacity-60"
      >
        {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
      </button>
    </div>
  );
}
